// ============================================================================
// Top-level router. The shell (NavShell) wraps every authenticated route.
//
// Option D IA (2026-06-13): the 24 flat modules collapsed into a pinned core,
// five flat ops modules, and three hubs — People · Money · Admin — each a
// layout route (HubLayout) with an Overview index + one child route per
// module. "My Route" subsumes Today / Template / Team Routes as tabs, and
// "My Work" merges the former /tasks + /inbox. Every legacy top-level path
// redirects (params preserved) into its new home, so existing deep links and
// in-app links keep working. Storage tables are NOT renamed — surface only.
// ============================================================================

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { NavShell } from './shared/nav-shell';
import { ForceLight } from './shared/force-light';
import { PublicPage } from './shared/public-page';
import {
  PeopleHub,
  MoneyHub,
  AdminHub,
  HubFirstChildRedirect,
  MoneyOverview,
  AdminOverview,
  RoutesLayout,
} from './shared/hub-layout';
import { PrefixRedirect } from './shared/legacy-redirect';
import { useAuth } from './shared/auth';
import { HUBS, hubIndexVisible } from './shared/nav-config';
// item #650: Home shows the work. The previous launcher (./modules/home/Home)
// stays in the repo, unrouted, for one release.
import Home from './modules/home/home-page';
import FacilityMapPage from './modules/map/FacilityMapPage';
import MyAccounts from './modules/account-management/my-accounts';
import FacilityDetail from './modules/account-management/facility-detail';
import StopService from './modules/account-management/stop-service';
import ClosedAccountsArchive from './modules/account-management/archive';
import SupplyRequestsPage from './modules/supply-management/supply-requests';
import SupplyApprovalQueue from './modules/supply-management/approval-queue';
import SupplyPOList from './modules/supply-management/po-list';
import SupplyPODetail from './modules/supply-management/po-detail';
import SupplyNewPOForm from './modules/supply-management/new-po-form';
import SupplyCatalogPage from './modules/supply-management/catalog-page';
import SupplyChemicalsPage from './modules/supply-management/chemicals-page';
import SupplyStockPage from './modules/supply-management/dialysis-stock/stock-page';
import SupplyStockPurchaseForm from './modules/supply-management/dialysis-stock/stock-purchase-form';
import BidsList from './modules/bids/bids-list';
import BidNew from './modules/bids/bid-new';
import BidDetail from './modules/bids/bid-detail';
import BidPricingSettings from './modules/bids/pricing-settings';
import Dashboard from './modules/dashboard/dashboard';
import IncentiveProgramPage from './modules/incentive';
import HiringPipeline from './modules/hr-hiring';
import {
  HrGuard,
  PeopleDesk,
  PeopleDirectory,
  PersonCard,
  HrEmployeeCard,
  HrPeopleHourly,
} from './modules/hr';
import { OnboardingPage } from './modules/hr-onboarding';
import {
  SeparationList,
  SeparationWizard,
  SeparationRecord,
  SeparationLooseEnds,
  SeparationItemLink,
} from './modules/hr-separation';
import { AmNightManagers, HoursLinksPage, HoursReportPage, TimeAdmin, TimeConsole, TimeLanding, TimePayRun, TimePunch, TimeRemoved, TimeReports, TimeTimecards, WorkerClockPage, KioskClockPage } from './modules/time-labor';
import { SubDirectory, SubDetail, SubcontractorsHub, NeedsStatusPage, DiscoveryPage, SignalPage } from './modules/subcontractors';
import NewAccountSopPage from './modules/new-account-sop';
import FinanceRecentChangesPage from './modules/finance-recent-changes';
import {
  SubReplacementList,
  SubReplacementForm,
  SubReplacementDetail,
  SubReplacementApprovalQueue,
} from './modules/sub-replacement';
import {
  ProjectList,
  ProjectDetail,
  AmEntryScreen,
  ClassificationQueue,
  FinanceFindings,
  QuotePreview,
} from './modules/project-work';
import {
  ContractList,
  ContractDetail,
  ContractForm,
} from './modules/contracts';
import { InvoicingQueue, InvoiceDetail } from './modules/invoicing';
import AdminFeedback from './modules/admin/feedback';
import AdminUsers from './modules/admin/users';
import AdminRateProposals from './modules/admin/rate-proposals';
import KickersPage from './modules/kickers';
import Demo from './modules/demo/Demo';
import { TaskDetail } from './modules/tasks';
import MyWork from './modules/my-work';
import MyWorkHistory from './modules/my-work/components/history-page';
import MyWorkNews from './modules/my-work/news-page';
import NotificationSettings from './modules/my-work/notification-settings';
import {
  // AM Collect archived 2026-07-02 — routes render the archived notice.
  AMCollectArchived,
} from './modules/am-collect-review';
import { CopilotPage } from './modules/copilot';
import { AMTodayRoute, RouteTemplateEditor, ExecRoutes } from './modules/am-routes';
import { SignerPage } from './modules/esign';
import { EnvelopesPage } from './modules/esign/envelopes-page';
import { EnvelopePreviewPage } from './modules/esign/envelope-preview';
import { EnvelopePrepPage } from './modules/esign/envelope-prep';
import { SubOnboardPortal } from './modules/sub-onboard-portal/portal-page';
import { SourcingIntakePage } from './modules/sourcing-intake/intake-page';
import { FleetRollup, VehicleDetail, EquipmentDetail, TagResolver } from './modules/fleet';
import { TAG_ROUTE_PATHS } from './modules/fleet/asset-tag';
import BuildRoomPage from './modules/build-room';
import ConnectPage from './modules/build-room/connect';

export default function App() {
  return (
    <Routes>
      {/* ---- Public, no shell ---------------------------------------------- */}
      {/* /demo — design system preview, no auth. Direct URL only. */}
      <Route
        path="/demo"
        element={
          <PublicPage>
            <Demo />
          </PublicPage>
        }
      />
      {/* /A/:assetId — PUBLIC. Where a physical asset QR tag lands.
          Registered UPPERCASE because that is what is printed: an all-caps URL
          encodes in QR alphanumeric mode (its charset has no lowercase), which
          buys a whole QR version for free. URL paths are case-sensitive, so the
          lowercase alias is registered too for anyone typing the id by hand off
          the label. Signed in → forwards to the asset's detail route; not
          signed in → renders "Property of TCS" with generic contact only, and
          makes no database call at all.
          The path list is BUILT from asset-tag.ts (TAG_PATH + LEGACY_TAG_PATHS)
          so the route table cannot drift from the string on the label, and so
          that every path that was ever printable keeps answering — a tag that
          404s once is a reprint, which is the one unrecoverable failure here. */}
      {TAG_ROUTE_PATHS.map((p) => (
        <Route
          key={p}
          path={`/${p}/:assetId`}
          element={
            <PublicPage>
              <TagResolver />
            </PublicPage>
          }
        />
      ))}
      {/* /sign/:token — PUBLIC signer page (E-Sign Phase 2).
          CW-J Phase 2, Ben's decision 6: e-Sign is PINNED LIGHT in every theme.
          It is the only surface people outside the company see and it renders a
          PDF onto a canvas — the document is white paper, so a dark frame round
          it reads as broken and a black signature on a dark field is worse.
          <ForceLight> re-declares the light channels on this subtree. */}
      <Route
        path="/sign/:token"
        element={
          <PublicPage>
            <ForceLight>
              <SignerPage />
            </ForceLight>
          </PublicPage>
        }
      />
      {/* /onboard/:token — PUBLIC sub-onboarding portal (N3). */}
      <Route
        path="/onboard/:token"
        element={
          <PublicPage>
            <SubOnboardPortal />
          </PublicPage>
        }
      />
      {/* /apply/:token — PUBLIC bilingual partner application (Sourcing).
          Tokenized QR/referral link; attribution comes from the token. */}
      <Route
        path="/apply/:token"
        element={
          <PublicPage>
            <SourcingIntakePage />
          </PublicPage>
        }
      />
      {/* /hours/:token — PUBLIC hours report for a facility admin or a sub
          (Time & Labor, Ben's Decision 2, 2026-08-28). The FA audits their
          hours self-serve on THEIR phone from an emailed link — no account.
          Pinned light for the same reason as /sign/:token: it is an
          outside-the-company surface. HOURS ONLY — the RPC payload carries no
          money-shaped key, and the page renders none. */}
      <Route
        path="/hours/:token"
        element={
          <PublicPage>
            <ForceLight>
              <HoursReportPage />
            </ForceLight>
          </PublicPage>
        }
      />
      {/* /clock — PUBLIC worker punch + my-hours page (TCSD-0140). Identity
          is mobile + HR-provisioned PIN inside the one anon-callable door
          (rpc_worker_clock); the first punch binds the phone; HR confirms the
          pairing before the personal hours view renders. Punching itself is
          never gated (D-03). Pinned light: an outside-the-app surface. */}
      <Route
        path="/clock"
        element={
          <PublicPage>
            <ForceLight>
              <WorkerClockPage />
            </ForceLight>
          </PublicPage>
        }
      />
      {/* /clock/kiosk — a BUILDING'S time clock on a shared iPad (item "Punch
          kiosk iPad", 2026-10-22). The web clip opens this address, so a kiosk is
          a kiosk by where it points. Not set up → "not set up", never phone mode.
          Set up → number + PIN, a photo, the iPad's building, then it clears. */}
      <Route
        path="/clock/kiosk"
        element={
          <PublicPage>
            <ForceLight>
              <KioskClockPage />
            </ForceLight>
          </PublicPage>
        }
      />

      {/* /build-room/connect — the Claude connector's consent screen (item #853).
          Supabase's sign-in server sends Ben here when he connects the command
          center on claude.ai. Outside the shell like the other pages people
          reach from elsewhere; it shows the app's own sign-in when signed out
          and refuses anyone who is not a superuser. Pinned light. */}
      <Route
        path="/build-room/connect"
        element={
          <PublicPage>
            <ForceLight>
              <ConnectPage />
            </ForceLight>
          </PublicPage>
        }
      />

      <Route element={<NavShell />}>
        {/* ===== Pinned core ============================================== */}
        <Route path="/" element={<Home />} />
        <Route path="/copilot" element={<CopilotPage />} />
        {/* My Work — unified Tasks + Inbox (Option D). */}
        <Route path="/my-work" element={<MyWork />} />
        {/* Done & archived — OUTSIDE the inbox. Ben, 2026-08-24: finished
            work leaves My Work; it is not a tab in it. */}
        <Route path="/my-work/history" element={<MyWorkHistory />} />
        {/* item #650 — what the old My Work tabs held, now reached from More. */}
        <Route path="/my-work/news" element={<MyWorkNews />} />
        <Route path="/my-work/notifications" element={<NotificationSettings />} />
        {/* Ben, 7 Oct 2026 (option D): the old Command Center and Pipelines are
            retired and land on My Work; the task list is My Work's Tasks view. */}
        <Route path="/my-work/command" element={<Navigate to="/my-work" replace />} />
        <Route path="/my-work/pipelines" element={<Navigate to="/my-work" replace />} />
        <Route path="/my-work/tasks" element={<TasksView />} />
        <Route path="/dashboard" element={<Dashboard />} />
        {/* Facility map (WS12) — full-screen consumer of the shared
            <FacilityMap> primitive; opens centred on the user. */}
        <Route path="/map" element={<FacilityMapPage />} />

        {/* ===== Flat ops modules ======================================== */}
        {/* Account Management */}
        <Route path="/account-management" element={<MyAccounts />} />
        <Route path="/account-management/archive" element={<ClosedAccountsArchive />} />
        <Route
          path="/account-management/facilities/:facilityId"
          element={<FacilityDetail />}
        />
        {/* Stop Service — pause or terminate ONE facility. A real route, not a
            modal, so it can be linked to from the two places an AM looks (the
            Actions tab and the Info tab) without either of them owning a copy
            of the form, and so a notification can deep-link straight to it. */}
        <Route
          path="/account-management/facilities/:facilityId/stop-service"
          element={<StopService />}
        />

        {/* My Route — Today / Template / Team Routes as tabs. */}
        <Route path="/route" element={<RoutesLayout />}>
          <Route index element={<AMTodayRoute />} />
          <Route path="template" element={<RouteTemplateEditor />} />
          <Route path="team" element={<ExecRoutes />} />
        </Route>

        {/* Supply Management */}
        <Route path="/supply" element={<SupplyRequestsPage />} />
        <Route path="/supply/approvals" element={<SupplyApprovalQueue />} />
        <Route path="/supply/orders" element={<SupplyPOList />} />
        <Route path="/supply/orders/new" element={<SupplyNewPOForm />} />
        <Route path="/supply/orders/new-stock" element={<SupplyStockPurchaseForm />} />
        <Route path="/supply/stock" element={<SupplyStockPage />} />
        <Route path="/supply/orders/:poId" element={<SupplyPODetail />} />
        <Route path="/supply/catalog" element={<SupplyCatalogPage />} />
        <Route path="/supply/chemicals" element={<SupplyChemicalsPage />} />

        {/* Bids — intake to executive approval */}
        <Route path="/bids" element={<BidsList />} />
        <Route path="/bids/new" element={<BidNew />} />
        <Route path="/bids/settings" element={<BidPricingSettings />} />
        <Route path="/bids/:bidId" element={<BidDetail />} />

        {/* Project Work */}
        <Route path="/project-work" element={<ProjectList />} />
        <Route path="/project-work/new" element={<AmEntryScreen />} />
        <Route path="/project-work/queue" element={<ClassificationQueue />} />
        <Route path="/project-work/findings" element={<FinanceFindings />} />
        <Route path="/project-work/:projectId" element={<ProjectDetail />} />
        <Route path="/project-work/:projectId/quote" element={<QuotePreview />} />

        {/* Fleet & Assets — vehicles + floor machines on one backbone.
            Rollup is fleet-admin gated in-page; vehicle detail is reachable by
            the assigned driver via a compliance notification (RLS scopes data). */}
        <Route path="/fleet" element={<FleetRollup />} />
        <Route path="/fleet/vehicle/:vehicleId" element={<VehicleDetail />} />
        {/* Where a floor-machine QR tag lands. ?src=qr leads with the
            confirm question; without it the page leads with the actions. */}
        <Route path="/fleet/equipment/:machineId" element={<EquipmentDetail />} />

        {/* Time & Labor — Buddy Punch replacement, package one (pilot).
            Landing page is the EXCEPTION QUEUE, not a menu (spec §5 Part 3).
            Nav is gated to hr/executive/superuser/finance; RLS on the punch
            tables is the real enforcement. No punch is ever refused for a
            location reason anywhere in this module (D-03), and no per-worker
            OT control exists (D-01 — the rules sit inert in labor_rules). */}
        {/* Two landing pages, one route: HR keeps the exception queue, an
            account manager gets Hours (2026-09-21). */}
        <Route path="/time" element={<TimeLanding />} />
        {/* The exception queue for a seat that lands on Hours (the national AM,
            Ben 2026-10-26, #981). HR keeps it at /time. */}
        <Route path="/time/exceptions" element={<TimeConsole />} />
        <Route path="/time/night-managers" element={<AmNightManagers />} />
        {/* Client views became Hours links (Ben, 2026-10-23) — one place for every full employee. */}
        <Route path="/time/hours-links" element={<HoursLinksPage />} />
        <Route path="/time/client-views" element={<Navigate to="/time/hours-links" replace />} />
        <Route path="/time/timecards" element={<TimeTimecards />} />
        <Route path="/time/removed" element={<TimeRemoved />} />
        <Route path="/time/punch" element={<TimePunch />} />
        <Route path="/time/admin" element={<TimeAdmin />} />
        {/* Package three: payroll generators — record-keeping in period_exports,
            D-05 (unverified rate → hours, never dollars) enforced in the RPCs. */}
        <Route path="/time/reports" element={<TimeReports />} />
        {/* View four: the pay period, its switches, and the ONE export. Opening
            it records nothing — only Export writes the versioned ledger row. */}
        <Route path="/time/pay-run" element={<TimePayRun />} />

        {/* New Accounts — renamed surface over the new_account_sop module.
            Full reframe lands in PR-4; here the route + label change only. */}
        <Route path="/new-accounts" element={<NewAccountSopPage />} />
        <Route path="/new-accounts/needs-info" element={<NewAccountSopPage initialTab="needs-info" />} />

        {/* ===== People hub ============================================== */}
        <Route path="/people" element={<PeopleHub />}>
          {/* Item 818 (Ben, 2026-10-07): /people is the Desk — HR's queue — for HR,
              the executives and superusers (HrGuard's roles); everybody else lands on
              the first People tab they can see. The generic Overview is gone. */}
          <Route index element={<PeopleIndex />} />
          {/* One directory and one card for every person. HrGuard (hr / executive /
              superuser) — the same gate the salaried-only directory had. */}
          <Route path="directory" element={<HrGuard />}>
            <Route index element={<PeopleDirectory />} />
            <Route path=":group" element={<PersonCard />} />
          </Route>
          {/* Sourcing is the Subcontractors hub's Pipeline tab now (item #683). */}
          <Route path="sourcing" element={<Navigate to="/people/subcontractors?view=pipeline" replace />} />
          <Route path="hiring" element={<HiringPipeline />} />
          <Route path="onboarding" element={<OnboardingPage />} />
          {/* The Subcontractors hub (item #683): My subs · Pipeline · Compliance · More.
              The HR board is its Compliance tab, unchanged. */}
          <Route path="subcontractors" element={<SubcontractorsHub />} />
          <Route path="subcontractors/needs-status" element={<NeedsStatusPage />} />
          <Route path="subcontractors/discovery" element={<DiscoveryPage />} />
          <Route path="subcontractors/signal" element={<SignalPage />} />
          {/* Directory must precede :providerId so "directory" isn't captured as an id */}
          <Route path="subcontractors/directory" element={<SubDirectory />} />
          <Route path="subcontractors/:providerId" element={<SubDetail />} />
          {/* ⛔ Separations sit OUTSIDE HrGuard on purpose. HrGuard allows hr,
              executive and superuser only, and three of the five checklist
              owners — Supervisor, National AM, Finance — are none of those.
              They are exactly the people the "My items" view exists for, so a
              guard here would lock out most of the module's users. RLS on
              employee_separations and separation_checklist_items is the real
              enforcement: it returns the record to HR, the executives and
              Finance, and everybody else only the items they hold. "new" and
              "loose-ends" precede :separationId so neither is captured as an id. */}
          <Route path="separations" element={<SeparationList />} />
          <Route path="separations/new" element={<SeparationWizard />} />
          <Route path="separations/loose-ends" element={<SeparationLooseEnds />} />
          <Route path="separations/item/:itemId" element={<SeparationItemLink />} />
          <Route path="separations/:separationId" element={<SeparationRecord />} />
          <Route path="hr" element={<HrGuard />}>
            {/* The HR tab is gone (item 818): its dashboard and the salaried-only
                directory both land on the People directory. The salaried
                employee CARD keeps its path — the person card links to it and
                it is the salaried record's editor. */}
            <Route index element={<Navigate to="/people/directory" replace />} />
            <Route path="directory" element={<Navigate to="/people/directory" replace />} />
            <Route path="directory/:employeeId" element={<HrEmployeeCard />} />
            {/* The two email-heuristic screens this replaced. Both filtered on
                employment_type='W2' and were only complementary on the email
                test, so anybody who was not W2 appeared in NEITHER — two
                Subcontractors were already invisible. Redirected rather than
                deleted because both paths are linked from elsewhere and
                bookmarked; the directory answers the same question correctly. */}
            <Route path="people/full" element={<Navigate to="/people/hr/directory" replace />} />
            {/* NOT a redirect. people/hourly kept its Partner-Sourced surface:
                partner workers come from service_assignments, have no employees
                row, and appear nowhere else. Its W2 tab is gone. */}
            <Route path="people/hourly" element={<HrPeopleHourly />} />
            {/* The old read-only detail page is GONE (2026-08-25, Ben's call).
                Convert-to-client-hire and the notes section moved onto the card,
                so it held nothing the card does not, and leaving it routed left
                two employee pages to drift apart. Old links land on the card. */}
            <Route
              path="people/full/:employeeId"
              element={<PrefixRedirect from="/people/hr/people/full" to="/people/hr/directory" />}
            />
            {/* Legacy HR sub-tabs that were retired earlier still bounce to
                the canonical standalone surfaces. */}
            <Route path="people/subs" element={<Navigate to="/people/subcontractors" replace />} />
            <Route path="onboarding" element={<Navigate to="/people/onboarding" replace />} />
          </Route>
        </Route>

        {/* ===== Money hub =============================================== */}
        <Route path="/money" element={<MoneyHub />}>
          <Route index element={<MoneyOverview />} />
          <Route path="invoicing" element={<InvoicingQueue />} />
          <Route path="invoicing/:invoiceId" element={<InvoiceDetail />} />
          <Route path="contracts" element={<ContractList />} />
          <Route path="contracts/new" element={<ContractForm />} />
          <Route path="contracts/:contractId" element={<ContractDetail />} />
          <Route path="envelopes" element={<EnvelopesPage />} />
          {/* Both of these render the document itself, so they are pinned
              light for the same reason as /sign/:token above. */}
          <Route
            path="envelopes/:envelopeId"
            element={
              <ForceLight>
                <EnvelopePrepPage />
              </ForceLight>
            }
          />
          {/* Preview as signer — the real signer view, no side effects. */}
          <Route
            path="envelopes/:envelopeId/preview"
            element={
              <ForceLight>
                <EnvelopePreviewPage />
              </ForceLight>
            }
          />
          <Route path="recent-changes" element={<FinanceRecentChangesPage />} />
          <Route path="incentives" element={<IncentiveProgramPage />} />
          <Route path="rate-proposals" element={<AdminRateProposals />} />
          <Route path="kickers" element={<KickersPage />} />
        </Route>

        {/* ===== Admin hub ============================================== */}
        <Route path="/admin" element={<AdminHub />}>
          <Route index element={<AdminOverview />} />
          {/* AM Collect archived 2026-07-02 — all routes render archived notice; data retained. */}
          <Route path="am-collect" element={<AMCollectArchived />} />
          <Route path="am-collect/history" element={<AMCollectArchived />} />
          <Route path="am-collect/:submissionId" element={<AMCollectArchived />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="feedback" element={<AdminFeedback />} />
        </Route>

        {/* The command center (item 701, TCSD-0412) — superuser only. The page
            refuses anyone else the way /admin/feedback does, and the bridge it
            reads answers 403 to them regardless. */}
        <Route path="/build-room" element={<BuildRoomPage />} />

        {/* ===== Contextual routes (not in nav) ========================== */}
        {/* Task detail — opened from the My Work Tasks tab + facility tabs. */}
        <Route path="/tasks/:taskId" element={<TaskDetail />} />
        {/* Sub Replacement — reached from the Subcontractors actions bar +
            facility "Change sub"; no top-level nav entry. */}
        <Route path="/sub-replacement" element={<SubReplacementList />} />
        <Route path="/sub-replacement/new" element={<SubReplacementForm />} />
        <Route path="/sub-replacement/approval-queue" element={<SubReplacementApprovalQueue />} />
        <Route path="/sub-replacement/:requestId" element={<SubReplacementDetail />} />

        {/* ===== "Coming soon" (off-nav, kept so direct links don't 404) === */}
        <Route path="/inspections" element={<ComingSoon name="Inspections" />} />
        <Route path="/bidding" element={<Navigate to="/bids" replace />} />

        {/* ===== Legacy redirects (params/search/hash preserved) ========= */}
        {/* My Work consolidation */}
        <Route path="/inbox" element={<Navigate to="/my-work" replace />} />
        <Route path="/tasks" element={<TasksView />} />
        {/* New Accounts rename */}
        <Route path="/new-account-sop/*" element={<PrefixRedirect from="/new-account-sop" to="/new-accounts" />} />
        {/* Help Chat → Ask AI */}
        <Route path="/help-chat" element={<Navigate to="/copilot" replace />} />
        {/* People hub */}
        <Route path="/hiring/*" element={<PrefixRedirect from="/hiring" to="/people/hiring" />} />
        <Route path="/onboarding/*" element={<PrefixRedirect from="/onboarding" to="/people/onboarding" />} />
        <Route path="/subcontractors/*" element={<PrefixRedirect from="/subcontractors" to="/people/subcontractors" />} />
        <Route path="/hr/*" element={<PrefixRedirect from="/hr" to="/people/hr" />} />
        {/* Money hub */}
        <Route path="/contracts/*" element={<PrefixRedirect from="/contracts" to="/money/contracts" />} />
        <Route path="/invoicing/*" element={<PrefixRedirect from="/invoicing" to="/money/invoicing" />} />
        {/* TCSD-0102, 2026-08-21 — Billing Comms is retired as a destination.
            Both old paths land on the renamed module so nobody's bookmark
            404s. Delete these two after one release. */}
        <Route path="/finance-comms/*" element={<PrefixRedirect from="/finance-comms" to="/project-work" />} />
        <Route path="/money/billing-comms/*" element={<PrefixRedirect from="/money/billing-comms" to="/project-work" />} />
        <Route path="/incentive/*" element={<PrefixRedirect from="/incentive" to="/money/incentives" />} />
        <Route path="/admin/rate-proposals/*" element={<PrefixRedirect from="/admin/rate-proposals" to="/money/rate-proposals" />} />
        {/* My Work kicker items deep-link to /kickers */}
        <Route path="/kickers/*" element={<PrefixRedirect from="/kickers" to="/money/kickers" />} />
        {/* Team Routes → My Route Team tab */}
        <Route path="/team-routes/*" element={<PrefixRedirect from="/team-routes" to="/route/team" />} />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function ComingSoon({ name }: { name: string }) {
  return (
    <div className="mx-auto max-w-3xl p-10 text-center">
      <h1 className="text-2xl font-semibold text-tcs-text">{name}</h1>
      <p className="mt-2 text-sm text-tcs-text-muted">
        This module is on the roadmap — not yet available.
      </p>
    </div>
  );
}

function NotFound() {
  return (
    <div className="mx-auto max-w-3xl p-10 text-center">
      <h1 className="text-2xl font-semibold text-tcs-text">Page not found</h1>
    </div>
  );
}

// /people itself (item 818): the Desk for the roles that hold it, otherwise the
// first People tab the role can see. Reads the same nav tree as the tab strip,
// so the index tab and the index route can never disagree.
function PeopleIndex() {
  const { profile } = useAuth();
  if (hubIndexVisible(profile?.role, HUBS.people)) return <PeopleDesk />;
  return <HubFirstChildRedirect hubKey="people" />;
}

// The task list's old addresses (/tasks, /my-work/tasks) open My Work's Tasks view
// and keep their filters: the dashboard tiles link to /tasks?category=…&assignee=me,
// and the old redirect dropped everything after the "?".
function TasksView() {
  const { search } = useLocation();
  const q = new URLSearchParams(search);
  q.set('view', 'tasks');
  return <Navigate to={`/my-work?${q.toString()}`} replace />;
}

